import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Github,
  GitBranch,
  ArrowUp,
  ArrowDown,
  Link2,
  Unlink,
  History,
  AlertCircle,
  Check,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { githubRepoApi, githubAuthApi, type LinkRepositoryRequest, type GitCommit } from '@/api/github';
import { formatDistanceToNow } from 'date-fns';

interface ProjectGitHubPanelProps {
  projectId: string;
  projectSlug: string;
}

export function ProjectGitHubPanel({ projectId, projectSlug }: ProjectGitHubPanelProps) {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showCommitsModal, setShowCommitsModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: githubStatus } = useQuery({
    queryKey: ['github-status'],
    queryFn: () => githubAuthApi.getStatus(),
  });

  const { data: projectGithub, isLoading } = useQuery({
    queryKey: ['project-github', projectId],
    queryFn: () => githubRepoApi.getInfo(projectId),
    enabled: !!githubStatus?.connected,
  });

  const { data: gitStatus } = useQuery({
    queryKey: ['project-git-status', projectId],
    queryFn: () => githubRepoApi.getStatus(projectId),
    enabled: !!projectGithub?.linked,
    refetchInterval: 30000,
  });

  const pushMutation = useMutation({
    mutationFn: (message?: string) => githubRepoApi.push(projectId, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-github', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-git-status', projectId] });
    },
  });

  const pullMutation = useMutation({
    mutationFn: () => githubRepoApi.pull(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-github', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-git-status', projectId] });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: () => githubRepoApi.unlink(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-github', projectId] });
    },
  });

  if (!githubStatus?.connected) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center">
          <Github className="h-8 w-8 mx-auto mb-3 text-gray-400" />
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Connect your GitHub account to sync this project
          </p>
          <a
            href="/settings/github"
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Link2 className="h-4 w-4" />
            Connect GitHub
          </a>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading GitHub info...</span>
        </div>
      </div>
    );
  }

  if (!projectGithub?.linked) {
    return (
      <>
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-center">
            <Github className="h-8 w-8 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              This project isn't linked to a GitHub repository
            </p>
            <button
              onClick={() => setShowLinkModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100"
            >
              <Link2 className="h-4 w-4" />
              Link Repository
            </button>
          </div>
        </div>

        {showLinkModal && (
          <LinkRepositoryModal
            projectId={projectId}
            projectSlug={projectSlug}
            onClose={() => setShowLinkModal(false)}
          />
        )}
      </>
    );
  }

  const repo = projectGithub.repository!;
  const status = gitStatus;

  return (
    <>
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Github className="h-5 w-5" />
            <div>
              <a
                href={repo.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                {repo.fullName}
              </a>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <GitBranch className="h-3 w-3" />
                {repo.defaultBranch}
                {repo.isPrivate && (
                  <span className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 rounded">
                    Private
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => unlinkMutation.mutate()}
            disabled={unlinkMutation.isPending}
            className="p-2 text-gray-400 hover:text-red-500"
            title="Unlink repository"
          >
            <Unlink className="h-4 w-4" />
          </button>
        </div>

        {/* Sync Status */}
        {status && (
          <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="flex-1">
              {status.status === 'synced' && (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                  <Check className="h-4 w-4" />
                  <span>Up to date</span>
                </div>
              )}
              {status.status === 'ahead' && (
                <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                  <ArrowUp className="h-4 w-4" />
                  <span>{status.aheadBy} commit{status.aheadBy > 1 ? 's' : ''} ahead</span>
                </div>
              )}
              {status.status === 'behind' && (
                <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
                  <ArrowDown className="h-4 w-4" />
                  <span>{status.behindBy} commit{status.behindBy > 1 ? 's' : ''} behind</span>
                </div>
              )}
              {status.status === 'diverged' && (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                  <AlertCircle className="h-4 w-4" />
                  <span>Diverged ({status.aheadBy} ahead, {status.behindBy} behind)</span>
                </div>
              )}
              {status.hasUncommittedChanges && (
                <p className="text-xs text-gray-500 mt-1">
                  {status.uncommittedFiles.length} uncommitted change{status.uncommittedFiles.length > 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => pushMutation.mutate(undefined)}
            disabled={pushMutation.isPending || (!status?.hasUncommittedChanges && status?.aheadBy === 0)}
            className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pushMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowUp className="h-4 w-4" />
            )}
            Push
          </button>
          <button
            onClick={() => pullMutation.mutate()}
            disabled={pullMutation.isPending || (status?.behindBy === 0)}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pullMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowDown className="h-4 w-4" />
            )}
            Pull
          </button>
          <button
            onClick={() => setShowCommitsModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
          >
            <History className="h-4 w-4" />
            History
          </button>
        </div>

        {/* Last synced */}
        {repo.lastSyncedAt && (
          <p className="text-xs text-gray-500">
            Last synced {formatDistanceToNow(new Date(repo.lastSyncedAt), { addSuffix: true })}
          </p>
        )}
      </div>

      {showCommitsModal && (
        <CommitsModal
          projectId={projectId}
          onClose={() => setShowCommitsModal(false)}
        />
      )}
    </>
  );
}

// Link Repository Modal
function LinkRepositoryModal({
  projectId,
  projectSlug,
  onClose,
}: {
  projectId: string;
  projectSlug: string;
  onClose: () => void;
}) {
  const [action, setAction] = useState<'create' | 'link'>('create');
  const [repoName, setRepoName] = useState(projectSlug);
  const [isPrivate, setIsPrivate] = useState(true);
  const [selectedRepo, setSelectedRepo] = useState('');
  const queryClient = useQueryClient();

  const { data: repos, isLoading: loadingRepos } = useQuery({
    queryKey: ['github-repos'],
    queryFn: () => githubAuthApi.listRepos(1, 100),
    enabled: action === 'link',
  });

  const linkMutation = useMutation({
    mutationFn: (request: LinkRepositoryRequest) => githubRepoApi.link(projectId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-github', projectId] });
      onClose();
    },
  });

  const handleSubmit = () => {
    if (action === 'create') {
      linkMutation.mutate({
        action: 'create',
        repositoryName: repoName,
        isPrivate,
      });
    } else {
      linkMutation.mutate({
        action: 'link',
        repositoryFullName: selectedRepo,
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold mb-4">Link GitHub Repository</h3>

        <div className="space-y-4">
          <div className="flex gap-2">
            <button
              onClick={() => setAction('create')}
              className={`flex-1 py-2 px-4 rounded-lg border ${
                action === 'create'
                  ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              Create New
            </button>
            <button
              onClick={() => setAction('link')}
              className={`flex-1 py-2 px-4 rounded-lg border ${
                action === 'link'
                  ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              Link Existing
            </button>
          </div>

          {action === 'create' ? (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Repository Name</label>
                <input
                  type="text"
                  value={repoName}
                  onChange={(e) => setRepoName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Private repository</span>
              </label>
            </>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">Select Repository</label>
              {loadingRepos ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading repositories...
                </div>
              ) : (
                <select
                  value={selectedRepo}
                  onChange={(e) => setSelectedRepo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                >
                  <option value="">Select a repository...</option>
                  {repos?.map((repo) => (
                    <option key={repo.fullName} value={repo.fullName}>
                      {repo.fullName}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={linkMutation.isPending || (action === 'link' && !selectedRepo) || (action === 'create' && !repoName)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {linkMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {action === 'create' ? 'Create & Link' : 'Link Repository'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Commits Modal
function CommitsModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { data: commits, isLoading } = useQuery({
    queryKey: ['project-commits', projectId],
    queryFn: () => githubRepoApi.getCommits(projectId, 20),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-semibold">Commit History</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            </div>
          ) : commits?.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No commits yet</div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {commits?.map((commit) => (
                <div key={commit.sha} className="p-4">
                  <p className="font-medium text-sm">{commit.message}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                      {commit.shortSha}
                    </code>
                    <span>{commit.author}</span>
                    <span>•</span>
                    <span>{formatDistanceToNow(new Date(commit.date), { addSuffix: true })}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
